/**
 * ManagePlanModal (MANAGE-PLAN-2) — the full editor for a PEGASUS-NATIVE PLUS
 * plan (pegasus_plan_sale). Edit the plan's name, price, term, coverage dates and
 * status; manage its parts/accessories; pick which equipment it covers; keep an
 * internal-notes thread; and (scaffolded) take payments/refunds. Writes go to
 * Supabase only — ESC plans are handled manually (Seth, 2026-06-18), so this
 * surfaces only for plans created in Pegasus.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Save } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { SlideOver } from "@/components/ui/SlideOver";
import { useCustomerEquipment, EquipCardRow } from "./CustomerEquipment";
import { useEquipmentOverrides, useEditEquipmentFields } from "./equipmentOverrides";
import { buildEquipmentView, type ClassifiedEquip } from "@/lib/equipment";
import { PlanAccessoriesEditor } from "./PlanAccessoriesEditor";
import { PaymentsPanel } from "./PaymentsPanel";
import { InternalNotes } from "@/lib/internalNotes";

export interface EditablePlan {
  id: string;
  custno: string;
  locno: string | null;
  base_name: string | null;
  total: number;
  term: number;
  start_date: string | null;
  expire_date: string | null;
  status: string;
}

const NO_AGRS = new Set<string>();

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</div>;
}

/** Covered-equipment picker — same "uncheck to exclude" model as the ESC plan
 * Equipment tab, writing the per-unit on_plan override (shared mechanism).
 * Exported for MEM-SALE-02: the Plan Builder shows it at sale time too. */
export function CoveredEquipment({ custno, locno }: { custno: string; locno: string | null }) {
  const { data: equipRows } = useCustomerEquipment(custno, locno);
  const { data: equipOverrides } = useEquipmentOverrides(custno, locno);
  const setOnPlan = useEditEquipmentFields();
  const view = buildEquipmentView(equipRows ?? [], undefined, equipOverrides ?? undefined);
  const isOnPlan = (e: ClassifiedEquip) => {
    const ov = equipOverrides?.get(e.counter);
    if (ov && ov.on_plan != null) return ov.on_plan;
    return true; // Pegasus plan covers the home's current systems by default
  };
  const toggle = (e: ClassifiedEquip, on: boolean) =>
    setOnPlan.mutate({ esc_counter: e.counter, custno, locno, on_plan: on });

  const shown = [...view.systems, ...view.auxiliary];
  if (shown.length === 0) {
    return <p className="text-xs text-slate-500">No equipment on file — add units from the Equipment tab.</p>;
  }
  return (
    <div className="space-y-2">
      <p className="text-[11px] leading-relaxed text-slate-400">Pegasus covers the current systems automatically and follows replacements. Uncheck a unit to exclude it from this plan.</p>
      {shown.map((e) => (
        <EquipCardRow key={e.counter} e={e} checked={isOnPlan(e)} onToggle={() => toggle(e, !isOnPlan(e))} activeAgrNos={NO_AGRS} />
      ))}
    </div>
  );
}

export function ManagePlanModal({ open, onClose, plan }: { open: boolean; onClose: () => void; plan: EditablePlan }) {
  const qc = useQueryClient();
  const [name, setName] = useState(plan.base_name ?? "");
  const [total, setTotal] = useState(String(plan.total ?? 0));
  const [term, setTerm] = useState(String(plan.term ?? 1));
  const [start, setStart] = useState(plan.start_date ?? "");
  const [status, setStatus] = useState(plan.status ?? "sold");
  // MEMBERSHIPS-02 P1: expiry is COMPUTED (start + term) — the database
  // trigger enforces it, so the field only shows what will be stored.
  const expire = (() => {
    const t = Math.max(1, Math.round(Number(term) || 1));
    if (!start) return "";
    const d = new Date(start + "T00:00:00");
    if (Number.isNaN(d.getTime())) return "";
    d.setFullYear(d.getFullYear() + t);
    return d.toISOString().slice(0, 10);
  })();

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("pegasus_plan_sale")
        .update({
          base_name: name.trim() || null,
          total: Number(total) || 0,
          term: Math.max(1, Math.round(Number(term) || 1)),
          start_date: start || null,
          status,
        })
        .eq("id", plan.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pegasus_agreements", plan.custno] });
      // 360-AUDIT #9: the header Member badge + Overview Membership card read
      // these — without them a cancel/renew showed stale for minutes.
      qc.invalidateQueries({ queryKey: ["membership_card", plan.custno] });
      qc.invalidateQueries({ queryKey: ["customer_renewal", plan.custno] });
      onClose();
    },
  });

  const inputCls = "mt-1 block w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm text-slate-700 focus:border-brand-400 focus:outline-none";

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      size="lg"
      title={<span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /> Manage Plan</span>}
      subtitle={name || "PLUS plan"}
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">Saved in Pegasus · ESC isn&apos;t touched</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-hairline bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Plan details */}
        <section>
          <SectionHeader>Plan Details</SectionHeader>
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 block text-sm">
              <span className="text-slate-600">Plan name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="PLUS Plan" className={inputCls} />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Price (total)</span>
              <input type="number" min={0} step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} className={inputCls} />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Term (years)</span>
              <input type="number" min={1} value={term} onChange={(e) => setTerm(e.target.value)} className={inputCls} />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Start date</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Renews / expires <span className="text-slate-400">· computed</span></span>
              <input type="date" value={expire} readOnly title="Start date + term — the rule, not a field" className={inputCls + " bg-slate-50 text-slate-500"} />
            </label>
            <label className="col-span-2 block text-sm">
              <span className="text-slate-600">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="sold">Active</option>
                <option value="cancelled">Cancelled</option>
                {/* MANAGE-PLAN-3 (Seth 2026-10-06: "remove my test plus plan"):
                    made-in-error plans disappear from the account entirely —
                    Cancelled stays visible as history; Removed does not. */}
                <option value="removed">Removed (made in error)</option>
              </select>
            </label>
          </div>
        </section>

        {/* Parts & accessories (reuses the existing per-home editor) */}
        <section>
          <SectionHeader>Parts &amp; Accessories</SectionHeader>
          <PlanAccessoriesEditor custno={plan.custno} locno={plan.locno} />
        </section>

        {/* Covered equipment */}
        <section>
          <SectionHeader>Covered Equipment</SectionHeader>
          <CoveredEquipment custno={plan.custno} locno={plan.locno} />
        </section>

        {/* Internal notes thread */}
        <section>
          <InternalNotes entityType="plan" entityId={plan.id} placeholder="Internal note about this plan (renewal call, special terms, billing heads-up…)" />
        </section>

        {/* Autopay & billing (PAY-05 — the real thing: enrollment + consent here,
            the daily plan-autopay engine charges at renewal, ledger history below) */}
        <section>
          <PaymentsPanel plan={{ id: plan.id, custno: plan.custno }} />
        </section>
      </div>
    </SlideOver>
  );
}
